import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { Modal } from "bootstrap"
import { loadingOverlay, openModalFromTemplate } from "../lib/utils"

export default class extends Controller {
  static values = {
    url: String,
    uuid: String,
    styleKey: String,
    uuidEn: String,
    uuidDe: String,
    styleExample: Boolean,
    redirectEvents: String,
    closeEvents: String,
    autoOpen: String,
  }

  declare urlValue: string
  declare uuidValue: string
  declare styleKeyValue: string
  declare uuidEnValue: string
  declare uuidDeValue: string
  declare styleExampleValue: boolean
  declare hasUuidValue: boolean
  declare hasStyleKeyValue: boolean
  declare hasUuidEnValue: boolean
  declare hasUuidDeValue: boolean
  declare hasStyleExampleValue: boolean
  declare redirectEventsValue: string
  declare closeEventsValue: string
  declare autoOpenValue: string

  abortController: AbortController | null = null
  modalEl: Element | null = null

  connect() {
    this.abortController = new AbortController()
    if (this.autoOpenValue) {
      ;(this.element as HTMLElement).click()
    }

    if ((this.hasUuidValue || this.hasUuidEnValue || this.hasUuidDeValue) && !this.urlValue) {
      this.updateUrl()
    }
  }

  disconnect() {
    this.abortController.abort()
  }

  async open(event: Event) {
    event.preventDefault()

    if (this.hasUuidValue || this.hasUuidEnValue || this.hasUuidDeValue) {
      this.updateUrl()
    }

    if (!this.urlValue) {
      throw new Error("remoteModal urlValue is missing")
    }

    if (this.urlValue.startsWith("#")) {
      this.modalEl = await this.openLocalModal()
    } else {
      this.modalEl = await this.openRemoteModal()
    }

    if (this.redirectEventsValue) {
      this.redirectEventsValue.split(/ +/).forEach((eventName) => {
        this.modalEl.addEventListener(
          eventName,
          (event: CustomEvent) => {
            event.stopPropagation()
            this.element.dispatchEvent(
              new CustomEvent(eventName, {
                bubbles: event.bubbles,
                cancelable: event.cancelable,
                composed: true,
                detail: event.detail,
              }),
            )
          },
          { signal: this.abortController.signal },
        )
      })
    }

    if (this.closeEventsValue) {
      this.closeEventsValue.split(/ +/).forEach((eventName) => {
        this.modalEl.addEventListener(
          eventName,
          (event: CustomEvent) => {
            this.close()
          },
          { signal: this.abortController.signal },
        )
      })
    }
  }

  async openRemoteModal() {
    loadingOverlay.show(250)
    const response = await get(this.urlValue)
    loadingOverlay.hide()

    if (!response.ok) {
      throw new Error(`remoteModal failed to load ${this.urlValue}`)
    }

    const modalHtml = await response.text
    return openModalFromTemplate(modalHtml)
  }

  async openLocalModal() {
    const modalEl = document.querySelector(this.urlValue)

    if (!modalEl) {
      throw new Error(`Unable to find modal element ${this.urlValue}`)
    }

    Modal.getOrCreateInstance(modalEl).show()
    return modalEl
  }

  close() {
    if (!this.modalEl) {
      return
    }

    Modal.getOrCreateInstance(this.modalEl).hide()
  }

  updateUrl() {
    const language = this.currentLanguage()
    let uuid: string | null = null

    if (this.hasUuidEnValue || this.hasUuidDeValue) {
      if (language === "de" && this.hasUuidDeValue) {
        uuid = this.uuidDeValue
      } else if (this.hasUuidEnValue) {
        uuid = this.uuidEnValue
      } else if (this.hasUuidDeValue) {
        uuid = this.uuidDeValue
      }
    } else if (this.hasUuidValue) {
      uuid = this.uuidValue
    }

    if (!uuid) {
      return
    }

    const basePath = `/${language}/fragments/book_preview/${uuid}`
    const params = new URLSearchParams()

    if (this.hasStyleExampleValue && this.styleExampleValue) {
      params.append("style_example", "true")
    }

    const queryString = params.toString()
    this.urlValue = queryString ? `${basePath}?${queryString}` : basePath
  }

  private currentLanguage(): string {
    const checked = document.querySelector<HTMLInputElement>('input[name="book[language]"]:checked')
    return checked?.value || "en"
  }
}
